import { FilterGroup, Input } from '@lt/components';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { i18n, useTranslation } from '@lt/localization';
import Slider from 'rc-slider';
import cn from 'classnames';
import { debounce } from 'lodash';
import type { IFilter } from '../types';
import styles from './FilterCityDistance.module.css';

type BoundsType = {
  minDistance: number;
  maxDistance: number;
};

const FilterCityDistanceComponent: React.FC<IFilter> = ({
  data,
  onChange,
  onClear,
  className,
  isMobile,
}) => {
  const { filterName, filterLabel } = FilterCityDistance;
  const {
    hotel_count: hotelCount,
    max_distance: maxDistance,
    min_distance: minDistance,
    selected_max_distance: selectedDistance,
  } = data;

  const { t } = useTranslation();

  const [bounds, setBounds] = useState<BoundsType>({
    minDistance,
    maxDistance,
  });
  const [isActive, setIsActive] = useState(
    selectedDistance !== bounds.maxDistance,
  );
  const [trackValue, setTrackValue] = useState<number>(selectedDistance);
  const [inputValue, setInputValue] = useState(
    t('filters.distance.distanceKm', { distance: selectedDistance }),
  );

  const inputRef = useRef('');

  const changeHandler = useCallback(
    (value) => {
      const path = FilterCityDistance.filterName;
      onChange({ path, diff: { selected_max_distance: value } });
    },
    [onChange],
  );

  useEffect(() => {
    if (
      maxDistance !== bounds.maxDistance ||
      minDistance !== bounds.minDistance
    )
      setBounds({ minDistance, maxDistance });
    if (selectedDistance !== trackValue) {
      setTrackValue(selectedDistance);
      setInputValue(
        t('filters.distance.distanceKm', { distance: selectedDistance }),
      );
      if (selectedDistance === maxDistance) setIsActive(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minDistance, maxDistance, selectedDistance]);

  if (!hotelCount) return null;

  const debouncedChangeHandler = debounce(
    (value) => changeHandler(value),
    1000,
  );

  const handleSliderChange = (val) => {
    if (!isActive) setIsActive(true);
    setTrackValue(val);
    setInputValue(t('filters.distance.distanceKm', { distance: val }));
  };

  const handleInputChange = (e) => {
    if (!isActive) setIsActive(true);
    let raw = e.target.value;

    // нужно, чтобы можно было ввести только цифры и только одну точку
    raw = raw.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');

    // превращает 000003 в 0.3
    if (/^0\d+/.test(raw)) {
      const digits = raw.replace(/^0+/, '');
      if (digits) {
        raw = `0.${digits}`;
      }
    }

    if (raw !== '' && Number(raw) > maxDistance) raw = maxDistance;

    if (raw === '') {
      setTrackValue(bounds.minDistance);
      debouncedChangeHandler(bounds.minDistance);
      if (inputRef.current === bounds.minDistance.toString()) {
        setInputValue(raw);
        inputRef.current = raw;
      } else {
        inputRef.current = bounds.minDistance.toString();
        setInputValue(bounds.minDistance.toString());
      }
    } else {
      setInputValue(raw);
      setTrackValue(Number(raw));
      debouncedChangeHandler(raw);
    }
  };

  const handleSliderChangeComplete = (val) => {
    debouncedChangeHandler(val);
  };

  const handleClear = () => {
    onClear(filterName);
    setInputValue(t('filters.distance.distanceKm', { distance: maxDistance }));
    setTrackValue(maxDistance);
    setIsActive(false);
  };

  const onInputBlur = (e) => {
    if (e.target.value === '' || e.target.value < bounds.minDistance)
      setInputValue(
        t('filters.distance.distanceKm', { distance: bounds.minDistance }),
      );
    else
      setInputValue(
        t('filters.distance.distanceKm', { distance: e.target.value }),
      );
  };

  const onInputFocus = (e) => {
    setInputValue(e.target.value.replace(/[^0-9.]/g, ''));
  };

  return (
    <div>
      <FilterGroup
        className={className}
        label={filterLabel}
        showClearBtn={isActive}
        onClear={handleClear}
        isMobile={isMobile}
      >
        <div
          className={cn(styles.inputBlock, {
            [styles.inputBlockMobile]: isMobile,
          })}
        >
          <Input
            className={cn(styles.textContainer, {
              [styles.textContainerMobile]: isMobile,
            })}
            value={inputValue}
            onChange={handleInputChange}
            type="text"
            inputmode="decimal"
            min={bounds.minDistance}
            max={bounds.maxDistance}
            step={0.5}
            onBlur={onInputBlur}
            onFocus={onInputFocus}
          />
        </div>
        <div
          className={cn(styles.trackContainer, {
            [styles.trackContainerMobile]: isMobile,
          })}
        >
          <Slider
            defaultValue={selectedDistance}
            min={minDistance}
            step={0.5}
            max={maxDistance}
            value={trackValue}
            onChange={handleSliderChange}
            onChangeComplete={handleSliderChangeComplete}
            styles={{
              track: {
                height: isMobile ? 4 : 2,
                backgroundColor: 'var(--secondary)',
              },
              rail: {
                backgroundColor: '#FFE6DD',
                height: isMobile ? 4 : 2,
              },
              handle: {
                backgroundColor: 'var(--secondary)',
                borderColor: 'var(--secondary)',
                width: isMobile ? 24 : 16,
                height: isMobile ? 24 : 16,
                marginTop: isMobile ? '-10px' : '-7px',
                opacity: 1,
                boxShadow: 'none',
                border: 'none',
                zIndex: 0,
              },
            }}
          />
        </div>
      </FilterGroup>
    </div>
  );
};

export const FilterCityDistance = Object.assign(FilterCityDistanceComponent, {
  filterName: 'distance_to_city_center',
  filterLabel: i18n.t('filters.distanceToCityCenter.title'),
  valuesLabel: (data) =>
    i18n.t('filters.distance.distanceKm', {
      distance: data.selected_max_distance,
    }),
});
